import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { Box, Button, Chip, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import { useLocation, useNavigate } from "react-router";
import bkfHeaderImage from "../../assets/bkf/BKF.webp";
import { grants } from "../../backoffice.access_control";
import { useThemeColor } from "../../hooks/useThemeColor";
import { RoomIcon } from "../../icons/RoomIcon";
import { SchedulerIcon } from "../../icons/SchedulerIcon";
import { PageTitle } from "../../misc/PageTitle";
import { CardsGrid } from "../../misc/CardsGrid";
import { Column } from "../../misc/Column";
import { type BkfNavigationItem, getBkfNavigationItems } from "../../misc/bkfNavigation";
import { type DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { LoadingIndicator } from "../../misc/LoadingIndicator";

export function DrivingSchoolBKFOverview() {
  const drivingSchool = useRecordContext<DrivingSchool>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const primaryColor = useThemeColor("primary");
  const cardIcons: Record<BkfNavigationItem["id"], React.ReactElement> = {
    overview: <DashboardOutlinedIcon />,
    courses: <SchoolOutlinedIcon />,
    scheduler: <SchedulerIcon />,
    autoForms: <DescriptionOutlinedIcon />,
    courseTemplates: <LibraryBooksOutlinedIcon />,
    timetables: <CalendarMonthOutlinedIcon />,
    rooms: <RoomIcon />,
    fleet: <LocalShippingOutlinedIcon />,
  };

  if (!drivingSchool) {
    return <LoadingIndicator />;
  }

  const items = getBkfNavigationItems({
    drivingSchoolId: drivingSchool.id,
    drivingSchool,
    pathname,
    hasGrant: (grant) => grants.has(grant),
  }).filter((item) => item.id !== "overview");

  return (
    <Column data-testid="DrivingSchoolBKFOverview" gap={8}>
      <Box
        component="header"
        sx={{
          alignItems: "center",
          display: "grid",
          gap: { xs: 4, md: 8 },
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
          maxWidth: "1140px",
          mt: { xs: 0, md: 4 },
          mx: "auto",
          width: "100%",
        }}
      >
        <Box>
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, mb: 1 }}>
            <Chip
              color="warning"
              label="NEU"
              size="small"
              sx={{ bgcolor: "var(--autovio-color-orange)", fontWeight: 700 }}
            />
            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
              AUTOVIO für Bildungsträger
            </Typography>
          </Box>
          <PageTitle documentTitle="Alles für deine BKF-Ausbildung an einem Ort">
            <Typography
              component="h2"
              sx={{ fontSize: { xs: "24px", md: "32px" }, fontWeight: 700, lineHeight: 1.2, mb: "30px", mt: "10px" }}
            >
              Alles für deine BKF-Ausbildung an einem Ort
            </Typography>
          </PageTitle>
          <Typography variant="body1" sx={{ color: "text.primary", maxWidth: 640 }}>
            Organisiere Kurse, Termine und Ressourcen zentral und behalte deine gesamte Berufskraftfahrer-Ausbildung im
            Blick.
          </Typography>
          <Box component="ul" sx={{ display: "grid", gap: 1, mb: 0, mt: 3, p: 0 }}>
            <Box component="li" sx={{ alignItems: "flex-start", display: "flex", gap: 1.5, listStyle: "none" }}>
              <Box
                aria-hidden
                sx={{
                  alignItems: "center",
                  bgcolor: primaryColor.main,
                  borderRadius: "50%",
                  display: "flex",
                  flexShrink: 0,
                  height: 20,
                  justifyContent: "center",
                  mt: "2px",
                  width: 20,
                }}
              >
                <CheckIcon sx={{ color: "common.white", fontSize: 14 }} />
              </Box>
              <Typography component="span">BKF-Kurse mit Vorlagen und Stundenplänen schneller aufsetzen</Typography>
            </Box>
            <Box component="li" sx={{ alignItems: "flex-start", display: "flex", gap: 1.5, listStyle: "none" }}>
              <Box
                aria-hidden
                sx={{
                  alignItems: "center",
                  bgcolor: primaryColor.main,
                  borderRadius: "50%",
                  display: "flex",
                  flexShrink: 0,
                  height: 20,
                  justifyContent: "center",
                  mt: "2px",
                  width: 20,
                }}
              >
                <CheckIcon sx={{ color: "common.white", fontSize: 14 }} />
              </Box>
              <Typography component="span">Termine, Fahrlehrer und Unterrichtsräume einfach koordinieren</Typography>
            </Box>
            <Box component="li" sx={{ alignItems: "flex-start", display: "flex", gap: 1.5, listStyle: "none" }}>
              <Box
                aria-hidden
                sx={{
                  alignItems: "center",
                  bgcolor: primaryColor.main,
                  borderRadius: "50%",
                  display: "flex",
                  flexShrink: 0,
                  height: 20,
                  justifyContent: "center",
                  mt: "2px",
                  width: 20,
                }}
              >
                <CheckIcon sx={{ color: "common.white", fontSize: 14 }} />
              </Box>
              <Typography component="span">Fuhrpark und Ausbildungsressourcen übersichtlich verwalten</Typography>
            </Box>
          </Box>
          <Button
            component="a"
            endIcon={<ArrowDownwardIcon />}
            href="#bkf-functions"
            sx={{ mt: 4 }}
            variant="contained"
          >
            Entdecke die neuen Funktionen
          </Button>
        </Box>
        <Box
          alt="Fahrer in der Kabine eines Lastwagens"
          component="img"
          src={bkfHeaderImage}
          sx={{
            aspectRatio: "4 / 3",
            borderRadius: 2,
            display: "block",
            objectFit: "cover",
            width: "100%",
          }}
        />
      </Box>
      <Box component="section" id="bkf-functions" aria-label="BKF-Funktionen" sx={{ scrollMarginTop: "120px" }}>
        <CardsGrid
          sx={{
            maxWidth: "1140px",
            mx: "auto",
            width: "100%",
            "& > .MuiCard-root": { width: "100%" },
            "@media (max-width: 599.95px)": {
              "&&": { gridTemplateColumns: "minmax(0, 1fr)" },
            },
            "@media (min-width: 900px)": {
              "&&": { gridTemplateColumns: "repeat(4, minmax(0, 1fr))" },
            },
          }}
        >
          {items.map((item) => (
            <CardsGrid.Card key={item.id} onClick={item.disabledReason ? undefined : () => navigate(item.to)}>
              <Column gap={1.5} sx={{ height: "100%" }}>
                <Box
                  aria-hidden
                  sx={{
                    alignItems: "center",
                    bgcolor: item.disabledReason ? "action.disabledBackground" : primaryColor.main,
                    borderRadius: "50%",
                    color: item.disabledReason ? "action.disabled" : primaryColor.contrastText,
                    display: "flex",
                    flexShrink: 0,
                    fontSize: { xs: 20, md: 24 },
                    height: { xs: 36, md: 44 },
                    justifyContent: "center",
                    width: { xs: 36, md: 44 },
                    "& > svg": { fontSize: "inherit" },
                  }}
                >
                  {cardIcons[item.id]}
                </Box>
                <Typography variant="h6" component="h3">
                  {item.label}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {item.description}
                </Typography>
                <Box sx={{ alignItems: "center", display: "flex", mt: "auto" }}>
                  {item.disabledReason ? (
                    <Chip
                      icon={<AddIcon />}
                      label="Buchbar"
                      size="small"
                      sx={{
                        borderColor: "var(--autovio-color-green)",
                        color: "var(--autovio-color-green)",
                        "& .MuiChip-icon": { color: "inherit", ml: "4px" },
                      }}
                      variant="outlined"
                    />
                  ) : (
                    <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                      <Box
                        aria-hidden
                        sx={{
                          bgcolor: "success.main",
                          borderRadius: "50%",
                          flexShrink: 0,
                          height: 10,
                          outline: "2px solid color-mix(in srgb, var(--autovio-color-green) 20%, transparent)",
                          width: 10,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Aktiv
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Column>
            </CardsGrid.Card>
          ))}
        </CardsGrid>
      </Box>
    </Column>
  );
}
